/**
 * Billing: a Merchant's commercial terms (ADR-0066, M14-1).
 *
 * ## Why these are not part of `amendMerchantSchema`
 *
 * A Merchant's identifying facts and a Merchant's credit terms are edited by different people
 * for different reasons and answer to different facets — `directory` names and screens a
 * Merchant, `finance` decides what they may owe. Folding terms into the directory body would
 * make one route answer to two authorities, which is the split `fx-rates.controller.ts` already
 * refused for exchange rates.
 *
 * It also keeps the reason codes honest. `merchant.amended` derives its code from the
 * compliance consequence — `screening_subject_changed` or `display_only` — and a credit limit
 * is neither. It gets `billing_terms_changed`, so *when did this Merchant's terms last move*
 * is a question the log answers.
 *
 * ## Every field is nullable, and null is a decision
 *
 * Migration 0041's header carries the full argument. In short: a null payment term means **due
 * on issue**, a null credit limit means **no ceiling**, because a credit limit refuses nothing,
 * and a null tax registration means **not registered**. (A Courier's float reads the other way: a
 * Courier with no ceiling is never assigned, so the fleet console requires one. This comment said
 * "as `couriers.float_limit_minor` does" until 2026-09-30, register row 34.) None of the three
 * means "unknown", so the body sends `null` explicitly rather than omitting the key — the
 * whole-row-replace shape every other console body in this repository uses, so a field cleared
 * on screen is cleared in the row rather than silently kept.
 *
 * ## The pair, stated here and again in the database
 *
 * A credit limit and its currency are set together or not at all. `viewer.ts`'s
 * two-statements-of-one-rule arrangement: this schema refuses it with a path a screen can
 * render, and `merchants_credit_pair` refuses it for anything that reaches the table another
 * way. Deliberately **not** `.refine`d across the two fields here — the cross-field rule reads
 * better as a service refusal carrying `{ path: 'creditCurrency', code: 'credit_pair' }`, which
 * is the shape `couriers.service.ts` already uses for the identical rule on a Courier's float.
 */
import { z } from 'zod';
import { renderedText } from './rendered-text.js';
/** The currencies a credit ceiling may be set in: the ones the platform prices. */
export const CREDIT_CURRENCIES = ['USD', 'LBP', 'SYP'];
/**
 * The detail codes a billing refusal can carry, each with copy in three languages.
 *
 * An **array** rather than a bare union, so `config-coverage.test.ts` can require copy for
 * every member. It was a union until ADR-0064's slice, which meant a reason could ship with no
 * Arabic and nothing would fail — and M14-1 shipped `credit_pair` and `terms_unchanged` with no
 * console to render them, so no gate fired and neither had copy anywhere until M14-2.
 */
export const BILLING_ISSUE_CODES = [
    'credit_pair',
    'terms_unchanged',
    // Issuance (ADR-0066, M14-2). Two, and there is deliberately no `nothing_to_invoice`: a
    // period with nothing un-invoiced is a **successful** run that examined nothing, not a
    // refusal, and a code nothing can emit is the species this list exists to keep honest.
    'period_backwards',
    'merchant_sandbox',
    // Payment (ADR-0066, M14-3). `merchant_sandbox` is deliberately not reused: a sandbox
    // Merchant is refused issuance outright, so a sandbox invoice cannot exist and the code
    // would be unreachable here — which is why `nothing_to_invoice` was deleted rather than
    // translated one slice ago.
    'currency_mismatch',
    'overpays_invoice',
    'invoice_paid',
    'invoice_written_off',
    'received_in_future',
    // Settlement (ADR-0066, M14-4). `currency_mismatch`, `overpays_invoice`, `invoice_paid`
    // and `invoice_written_off` are reused verbatim — the invoice-side refusal is the same
    // question whether the credit came from a transfer or a netting. `received_in_future`
    // is not: its copy names money landing, which is false of a netting, so a netted
    // instant in the future gets its own code rather than a sentence that reads wrong.
    'settled_in_future',
    // ADR-0072: a payment or settlement reference, or a tax registration, that looks like a phone
    // number, or that holds a text-direction override. Carried by `validation_failed`;
    // `billingRefusal` renders every detail code from here.
    'phone_shaped',
    'direction_override',
    // ADR-0082: a VAT rate is in force for the Merchant's country and the platform records no
    // registration for it, so an invoice would carry tax without the supplier's VAT number.
    'vat_registration_missing',
];
/**
 * An invoice's lifecycle, as a status rather than a fourth 02a machine.
 *
 * **`draft` is deliberately absent.** Issuance is one act: the number is minted, the lines are
 * written and the event is appended in one transaction, so there is no moment at which a draft
 * exists — and a draft would need a nullable `series_number`, which is a second shape for the
 * one column that must never be ambiguous. A state nothing can reach reads as supported and is
 * not, which is the species `docs/decisions/README.md` catalogues.
 *
 * `issued`, `part_paid` and `paid` are reachable since M14-3, which brought the payment that
 * moves them and the `UPDATE (status)` grant that lets it (migration 0043). `written_off` is
 * still reachable by nothing: it is a decision nobody has been given a route to take, and
 * inventing one to fill the enum is the shape this comment already refuses above.
 */
export const INVOICE_STATUSES = ['issued', 'part_paid', 'paid', 'written_off'];
/**
 * Integer minor units as a string (I2), and `-` is unrepresentable rather than refused later.
 * Zero is admitted: a Merchant who may owe nothing is a real and different state from a
 * Merchant with no ceiling at all, which is `null`.
 */
const minorUnits = z.string().regex(/^\d{1,18}$/u, 'money_minor_units');
// Why the non-rendered strings below are exempt from the write-time phone check (ADR-0072).
const AMOUNT_REASON = 'minor units, digits only by regex; views show the amount re-read from a BIGINT column, never the text sent';
const INSTANT_REASON = 'an ISO instant by zod datetime; re-serialised with toISOString before it is stored or shown';
export const amendBillingTermsSchema = z
    .object({
    /** Days from issue to due. `null` is due on issue — see the header. */
    paymentTermsDays: z.number().int().min(0).max(365).nullable(),
    /** The most this Merchant may owe, in minor units. `null` is no ceiling. */
    creditLimitMinor: minorUnits.nullable(),
    creditCurrency: z.enum(CREDIT_CURRENCIES).nullable(),
    /** As it must appear on an invoice. `null` is a Merchant that is not registered. */
    taxRegistration: z.string().trim().min(1).max(64).nullable(),
    /** Why. Free text, because an amendment is a human act and the reason is evidence. */
    note: z.string().trim().min(1).max(500),
})
    .strict();
export const amendBillingTermsRendered = renderedText(amendBillingTermsSchema, {
    refs: ['taxRegistration'],
    exempt: {
        creditLimitMinor: AMOUNT_REASON,
        note: 'stored only in the merchant.amended event payload; no view renders it',
    },
});
/**
 * Issue a Merchant's invoices for one period (ADR-0066, M14-2).
 *
 * The body names a **period and nothing else**. It carries no amount and no list of postings,
 * on `remitSchema`'s argument: a caller who could name a figure could name a wrong one, and
 * what is owed is a fact in the ledger rather than a number a screen typed. It carries no
 * currency either — a Merchant may owe in more than one, and which ones is the ledger's answer,
 * not the caller's.
 *
 * Instants are sent as typed and parsed here rather than by a `Date` round trip, so a value the
 * API refuses is refused with the string the operator entered.
 */
export const issueInvoicesSchema = z
    .object({
    /** Half-open: [periodStart, periodEnd). Consecutive periods neither double- nor miss-count. */
    periodStart: z.string().datetime({ offset: true }),
    periodEnd: z.string().datetime({ offset: true }),
    /** Why now. Free text, because an issuance is a human act and the reason is evidence. */
    note: z.string().trim().min(1).max(500),
})
    .strict();
export const issueInvoicesRendered = renderedText(issueInvoicesSchema, {
    exempt: {
        periodStart: INSTANT_REASON,
        periodEnd: INSTANT_REASON,
        note: 'stored only in the invoice.issued event payload; no view renders it',
    },
});
/**
 * Record a payment against one invoice (ADR-0066, M14-3).
 *
 * Unlike an issuance, this body **does** name a figure, and it has to: nothing in this platform
 * observes money arriving. `PaymentProvider` is RESERVED with no TypeScript anywhere and COD is
 * the only rail, so a payment is an operator's assertion that a transfer happened — which makes
 * the amount a fact only they hold. What the API can still refuse is an amount that does not
 * fit the invoice, and it does.
 *
 * `reference` is **required and unvalidated**, which is ADR-0057's ruling carried over whole:
 * a remittance on a rail that moves no money refuses without the reference of the transfer the
 * operator made, and *"the platform cannot check a reference issued by a rail it does not talk
 * to"* (§10.6). What it buys is that `paid` is never the only thing standing behind the word.
 *
 * `receivedAt` is when the money landed, not when this was typed — the row records both, and
 * aging is measured against the first. Instants are sent as typed for `issueInvoicesSchema`'s
 * reason.
 */
export const recordPaymentSchema = z
    .object({
    /** Minor units of the invoice's own currency. Unsigned by the regex: a refund is not a
     *  payment with a sign, and this shape makes that unexpressible rather than refused. */
    amountMinor: minorUnits,
    /** Stated rather than inferred, so a mismatch is refused with a path an operator can see. */
    currency: z.enum(CREDIT_CURRENCIES),
    /** The transfer reference. Unvalidated beyond being present — see the header. */
    reference: z.string().trim().min(1).max(200),
    receivedAt: z.string().datetime({ offset: true }),
    /** Why. Free text, because recording a payment is a human act and the reason is evidence. */
    note: z.string().trim().min(1).max(500),
})
    .strict();
export const recordPaymentRendered = renderedText(recordPaymentSchema, {
    refs: ['reference'],
    exempt: {
        amountMinor: AMOUNT_REASON,
        receivedAt: INSTANT_REASON,
        note: 'stored only in the payment.received event payload; no view renders it',
    },
});
/**
 * Net a Merchant's own payable against one of their invoices (ADR-0066, M14-4).
 *
 * `recordPaymentSchema` verbatim except for two field names, and the difference is the whole
 * point: `reference` is required here too, but it names *what was netted* rather than a
 * bank's transfer id — there is no transfer, so there is nothing for the platform to check
 * either way, and the field stays exactly as unvalidated as it already was. `settledAt`
 * replaces `receivedAt` because nothing was *received* — the money never left the Merchant's
 * own payable — but it is read the same way: aging is measured against it, so a netting
 * entered a week after the decision would otherwise read as later than it was.
 *
 * `currency` still has to match the invoice: both legs of `settlementNettedPostings` are the
 * one currency the caller names, because `merchant.payable` and `merchant.receivable` are
 * both dimensioned `['merchant', 'currency']` and neither side converts. A netting across two
 * currencies is not refused here — it is unexpressible, because there is only one currency in
 * the request to disagree with itself.
 */
export const recordSettlementSchema = z
    .object({
    /** Minor units of the invoice's own currency. Unsigned, for `recordPaymentSchema`'s
     *  reason: a reversal is a separate decision, not a settlement with a sign. */
    amountMinor: minorUnits,
    currency: z.enum(CREDIT_CURRENCIES),
    /** What was netted. Unvalidated beyond being present — there is no transfer to check. */
    reference: z.string().trim().min(1).max(200),
    settledAt: z.string().datetime({ offset: true }),
    /** Why. Free text, because netting a payable against a receivable is a human act. */
    note: z.string().trim().min(1).max(500),
})
    .strict();
export const recordSettlementRendered = renderedText(recordSettlementSchema, {
    refs: ['reference'],
    exempt: {
        amountMinor: AMOUNT_REASON,
        settledAt: INSTANT_REASON,
        note: 'stored only in the settlement.netted event payload; no view renders it',
    },
});
//# sourceMappingURL=billing.js.map